import React from "react";
import {IServerPrediction} from "modules/types";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {getDashedValue, prepareStatToRender} from "modules/utils/helpers";
import {useSelector} from "react-redux";
import {getActiveContestCategory, getPlayerById} from "modules/selectors";
import {Exists} from "components/Exists";
import {PlayerImage} from "components/PlayerImage";
import {PlayerImageType} from "modules/enums";
import {every, eq} from "lodash";

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	width: 100%;
	background: linear-gradient(180deg, rgba(41, 41, 41, 0.2) 0%, #2c2c2e 100%);
	border-radius: 8px;
	padding-top: 6px;
`;

const BottomStats = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 28px;
	background: ${({theme}) => theme.summaryCard.stats.background};
	color: ${({theme}) => theme.summaryCard.stats.color};
	-webkit-border-radius: 0 0 5px 5px;
	-moz-border-radius: 0 0 5px 5px;
	border-radius: 0 0 5px 5px;
	width: 100%;
	padding: 0 12px;

	> div {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		gap: 6px;
	}

	span,
	b {
		font-style: normal;
		font-weight: 400;
		font-size: 12px;
		line-height: 16px;
		color: ${({theme}) => theme.colors.text.white};
	}

	b {
		font-weight: bold;
		font-size: 13px;

		* {
			font-weight: bold;
		}

		&.correct * {
			color: ${({theme}) => theme.guess.correctColor};
		}

		&.incorrect * {
			color: ${({theme}) => theme.guess.incorrectColor};
		}
	}
`;

const PlayersBlock = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	height: 64px;
	width: 100%;
`;

const DoubleStats = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 6px;
	width: 102px;

	&.first {
		&.correct > div:first-of-type {
			background: ${({theme}) => theme.guess.correctColor};
		}

		&.incorrect > div:first-of-type {
			background: ${({theme}) => theme.guess.incorrectColor};
		}
	}

	&.second {
		&.correct > div:last-of-type {
			background: ${({theme}) => theme.guess.correctColor};
		}

		&.incorrect > div:last-of-type {
			background: ${({theme}) => theme.guess.incorrectColor};
		}
	}
`;

const StatBlock = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	width: 44px;
	height: 44px;
	background: rgba(255, 255, 255, 0.1);
	border-radius: 4px;

	font-family: ${({theme}) => theme.fonts.secondary};
	font-style: normal;
	font-weight: 700;
	font-size: 15px;
	line-height: 150%;
	color: ${({theme}) => theme.colors.text.white};

	text-align: center;
	letter-spacing: 0.0015em;
	font-feature-settings: "kern" off;
`;

const PlayerInfo = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	color: ${({theme}) => theme.colors.text.white};
	width: 50%;
	height: 100%;

	&.right {
		flex-direction: row-reverse;
		text-align: right;
	}

	img {
		height: auto;
		width: auto;
		max-width: 60px;
		margin-top: auto;
	}

	span.player-name {
		font-style: normal;
		font-weight: 400;
		font-size: 11px;
		line-height: 15px;
		color: ${({theme}) => theme.colors.text.white};

		.last-name {
			display: block;
			margin-top: 4px;
			font-weight: bold;
		}
	}
`;

const getPredictionClass = (prediction: IServerPrediction): string => {
	if (prediction.isCorrect === true) {
		return "correct";
	}

	if (prediction.isCorrect === false) {
		return "incorrect";
	}

	return "";
};

interface IProps {
	prediction: IServerPrediction;
	last: boolean;
}

export const SummaryCard: React.FC<IProps> = ({prediction, last}) => {
	const {t} = useTranslation();
	const category = useSelector(getActiveContestCategory);
	const predictionText = t(`summary.answer.${prediction.answer ?? "error"}.${category}`);

	const playerOne = useSelector(getPlayerById)(prediction.playerIdOne);
	const playerTwo = useSelector(getPlayerById)(prediction.playerIdTwo);

	const guessClass = getPredictionClass(prediction);

	if (!prediction) {
		return null;
	}

	return (
		<Wrapper>
			<PlayersBlock>
				<PlayerInfo className="left">
					<PlayerImage player={playerOne} type={PlayerImageType.headshot} />
					<span className="player-name">
						<span>
							{getDashedValue(playerOne?.firstName)} <br />{" "}
						</span>
						<span className="last-name">{getDashedValue(playerOne?.lastName)}</span>
					</span>
				</PlayerInfo>

				<DoubleStats className="center">
					<StatBlock>
						{prepareStatToRender(prediction.playerOneStats, category)}
					</StatBlock>
					<StatBlock>?</StatBlock>
				</DoubleStats>

				<PlayerInfo className="right">
					<PlayerImage player={playerTwo} type={PlayerImageType.headshot} />
					<span className="player-name">
						<span>
							{getDashedValue(playerTwo?.firstName)} <br />{" "}
						</span>
						<span className="last-name">{getDashedValue(playerTwo?.lastName)}</span>
					</span>
				</PlayerInfo>
			</PlayersBlock>

			<BottomStats>
				<div>
					<span>{t("page.summary.guess")}:</span>
					<b className={guessClass}>
						<span>{predictionText}</span>
						<Exists when={every([eq(prediction?.isCorrect, false), last])}>
							<span>
								&nbsp;({prepareStatToRender(prediction?.playerTwoStats, category)})
							</span>
						</Exists>
					</b>
				</div>
			</BottomStats>
		</Wrapper>
	);
};
